import { Link } from 'react-router-dom';
import { Button } from '@/components/Button';
import { cn } from '@/lib/cn';
import { downloadWithCompanyHeader } from '@/lib/download';
import type { ModuleResponseSummary } from '@/lib/assessments';

type Props = {
  modules: ModuleResponseSummary[];
  /** When true, rows are clickable links to /module/:id. Off for frozen snapshots. */
  linked: boolean;
  /** Hide the CSV button on frozen views. */
  showCsvExport?: boolean;
};

export function ModuleRollup({ modules, linked, showCsvExport = true }: Props) {
  const sorted = [...modules].sort((a, b) => a.displayOrder - b.displayOrder);
  return (
    <section className="rounded-lg border border-border bg-surface" data-testid="module-rollup">
      <header className="flex items-center justify-between gap-3 px-7 py-5">
        <h2 className="font-display text-h3 font-semibold">Modules</h2>
        {showCsvExport ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              downloadWithCompanyHeader('/api/Snapshots/export.csv', 'hexdi-items.csv')
            }
          >
            Export CSV
          </Button>
        ) : null}
      </header>
      <ul className="divide-y divide-border">
        {sorted.map((m) => {
          const total = m.summary.itemResponses.total;
          const ok = m.summary.itemResponses.meetsStandardCount;
          const pct = total > 0 ? Math.round((ok / total) * 100) : 0;
          const deficiencies = total - ok;
          const Inner = (
            <div className="grid grid-cols-[auto_1fr_auto_auto_auto] items-center gap-4 px-7 py-4">
              <span className="rounded bg-text-primary px-2 py-0.5 text-caption font-bold text-white">
                {m.code}
              </span>
              <p className="font-medium text-text-primary">{m.name}</p>
              <span className="text-body-sm text-text-secondary">
                {deficiencies > 0 ? `${deficiencies} deficient` : 'All clear'}
              </span>
              <div className="flex w-40 items-center gap-3">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-tint">
                  <div className="h-full bg-accent" style={{ width: `${pct}%` }} />
                </div>
                <span className="w-10 text-right text-body-sm font-medium">
                  {pct}%
                </span>
              </div>
              {linked ? (
                <span className="text-body-sm text-text-secondary">→</span>
              ) : (
                <span className="w-3" />
              )}
            </div>
          );
          return linked ? (
            <li key={m.id}>
              <Link
                to={`/module/${m.id}`}
                className={cn('block hover:bg-background')}
              >
                {Inner}
              </Link>
            </li>
          ) : (
            <li key={m.id}>{Inner}</li>
          );
        })}
      </ul>
    </section>
  );
}
